import { ReflectUtils } from "@dooboostore/core";
import { ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
import { findAllStateMetadata } from "./state.js";
import { ElementApply } from "@dooboostore/core-web";
const APPLY_NODE_METADATA_KEY = Symbol.for("simple-web-component:apply-node");
const resolveSelector = (selector, inst, win) => {
  if (typeof selector === "function") {
    const hostSet = SwcUtils.getHelperAndHostSet(inst);
    const result = selector(inst, hostSet);
    if (result instanceof win.Element) {
      return result;
    }
    if (result instanceof win.NodeList) {
      return result.length > 0 ? result[0] : null;
    }
    if (Array.isArray(result)) {
      return result.length > 0 ? result[0] : null;
    }
    if (result === null) {
      return null;
    }
    return result;
  }
  return selector;
};
const applyToDom = (currentThis, targetEl, res, pos, win, host) => {
  if (!targetEl) return;
  if (pos === "clearChildren") {
    targetEl.replaceChildren();
    return [];
  }
  if (pos === "remove") {
    (targetEl instanceof win.ShadowRoot ? targetEl.host : targetEl).remove();
    return [];
  }
  const doc = win.document;
  const hostSet = SwcUtils.getHelperAndHostSet(currentThis);
  const id = currentThis._swcId;
  const toNodes = (v) => {
    if (v === null || v === void 0 || v === false) return [];
    if (typeof v === "object" && typeof v.nodeType === "number") return [v];
    if (typeof v === "object" && typeof v.length === "number") return Array.from(v).flatMap(toNodes);
    if (pos === "innerText") return [doc.createTextNode(String(v))];
    const t = doc.createElement("template");
    t.innerHTML = String(v);
    return Array.from(t.content.childNodes);
  };
  const nodes = toNodes(res);
  SwcUtils.projectProcessHtml(id, nodes, doc);
  const isShadowRoot = targetEl instanceof win.ShadowRoot;
  if (pos === "replace") {
    if (!isShadowRoot) targetEl.replaceWith(...nodes);
  } else if (pos === "innerHtml") {
    targetEl.replaceChildren(...nodes);
  } else if (pos === "innerText") {
    targetEl.replaceChildren(...nodes);
  } else if (pos === "replaceChildren") {
    targetEl.replaceChildren(...nodes);
  } else {
    if (pos === "beforeEnd") {
      targetEl.append(...nodes);
    } else if (pos === "afterBegin") targetEl.prepend(...nodes);
    else if (pos === "beforeBegin" && !isShadowRoot) targetEl.before(...nodes);
    else if (pos === "afterEnd" && !isShadowRoot) targetEl.after(...nodes);
  }
  const stateContext = { ...hostSet };
  findAllStateMetadata(currentThis).forEach((it) => {
    stateContext[it.name] = currentThis[it.propertyKey];
  });
  new ElementApply(targetEl, { id }).apply({ target: "noInitialized", context: stateContext, bind: currentThis });
  return nodes;
};
function applyNode(selectorOrOptions, maybeOptions) {
  let selector;
  let options;
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    selector = selectorOrOptions;
    options = maybeOptions || { position: "replaceChildren" };
  } else {
    selector = "$this";
    options = selectorOrOptions || { position: "replaceChildren" };
  }
  return (targetObj, propertyKey, descriptor) => {
    const constructor = targetObj.constructor;
    let metaList = ReflectUtils.getOwnMetadata(APPLY_NODE_METADATA_KEY, constructor);
    if (!metaList) {
      metaList = /* @__PURE__ */ new Map();
      ReflectUtils.defineMetadata(APPLY_NODE_METADATA_KEY, metaList, constructor);
    }
    metaList.set(propertyKey, { propertyKey, selector, options });
    const original = descriptor.value;
    descriptor.value = function(...args) {
      ensureInit(this);
      const currentWin = SwcUtils.resolveWindow(this);
      const currentDoc = currentWin.document;
      const hostSet = SwcUtils.getHelperAndHostSet(this);
      const getTarget = () => {
        const r = options.root || "auto";
        const applyRoot = (t) => {
          if (!t) return t;
          if (r === "auto") return t.shadowRoot || t;
          if (r === "shadow") return t.shadowRoot;
          if (r === "light") return t;
          if (r === "all") return t.shadowRoot || t;
          return t;
        };
        const resolved = resolveSelector(selector, this, currentWin);
        if (resolved instanceof currentWin.Element) {
          return applyRoot(resolved);
        }
        if (resolved === null) {
          return null;
        }
        const stringSelector = resolved;
        if (stringSelector === "$this") return applyRoot(this);
        if (stringSelector === "$host") return applyRoot(hostSet.$host);
        if (stringSelector === "$parentHost") return applyRoot(hostSet.$parentHost);
        if (stringSelector === "$appHost") return applyRoot(hostSet.$appHost);
        if (stringSelector === "$firstHost") return applyRoot(hostSet.$firstHost);
        if (stringSelector === "$lastHost") return applyRoot(hostSet.$lastHost);
        const targetRoot = applyRoot(this);
        return targetRoot ? targetRoot.querySelector(stringSelector) : null;
      };
      const targetEl = getTarget();
      const res = original.apply(this, args);
      const extractValue = (v) => {
        const keyToUse = options.valueKey ?? APPLY_NODE_METADATA_KEY;
        if (v && typeof v === "object" && keyToUse in v) {
          return v[keyToUse];
        }
        return v;
      };
      const decide = (raw) => {
        if (options.position === "remove" || options.position === "clearChildren") {
          if (options.valueKey === void 0) return true;
          return raw && typeof raw === "object" ? raw[options.valueKey] : void 0;
        }
        return extractValue(raw);
      };
      const runApply = (target, val) => {
        const pos = options.position || "beforeEnd";
        if ((pos === "remove" || pos === "clearChildren") && !val) return;
        const processedVal = val;
        if (!options.filter || options.filter(target, processedVal, { currentThis: this, helper: hostSet })) {
          applyToDom(this, target, processedVal, pos, currentWin);
        }
      };
      const fallbackNodes = [];
      if (targetEl && options.fallback) {
        const loadingRes = options.fallback.call(this, hostSet);
        fallbackNodes.push(...applyToDom(this, targetEl, loadingRes, options.position || "beforeEnd", currentWin));
      }
      if (res instanceof Promise) {
        return res.then((finalRes) => {
          const extracted = decide(finalRes);
          fallbackNodes.forEach((it) => it.remove());
          if (extracted !== void 0 && targetEl) runApply(targetEl, extracted);
          return finalRes;
        });
      } else {
        const extracted = decide(res);
        if (extracted !== void 0 && targetEl) runApply(targetEl, extracted);
        return res;
      }
    };
  };
}
function replaceChildren(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "replaceChildren" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "replaceChildren" });
  }
  return applyNode({ ...selectorOrOptions, position: "replaceChildren" });
}
function clearChildrenNode(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "clearChildren" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "clearChildren" });
  }
  return applyNode({ ...selectorOrOptions, position: "clearChildren" });
}
const clearNode = clearChildrenNode;
function removeNode(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "remove" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "remove" });
  }
  return applyNode({ ...selectorOrOptions, position: "remove" });
}
function innerHtml(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerHtml" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerHtml" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerHtml" });
}
function innerHtmlLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerHtml", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerHtml", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerHtml", root: "light" });
}
function innerHtmlShadow(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerHtml", root: "shadow" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerHtml", root: "shadow" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerHtml", root: "shadow" });
}
function innerText(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerText" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerText" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerText" });
}
function innerTextLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerText", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerText", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerText", root: "light" });
}
function innerTextShadow(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "innerText", root: "shadow" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "innerText", root: "shadow" });
  }
  return applyNode({ ...selectorOrOptions, position: "innerText", root: "shadow" });
}
function insertBeforeEnd(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "beforeEnd" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "beforeEnd" });
  }
  return applyNode({ ...selectorOrOptions, position: "beforeEnd" });
}
function insertBeforeEndLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "beforeEnd", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "beforeEnd", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "beforeEnd", root: "light" });
}
function insertBeforeEndShadow(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "beforeEnd", root: "shadow" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "beforeEnd", root: "shadow" });
  }
  return applyNode({ ...selectorOrOptions, position: "beforeEnd", root: "shadow" });
}
function insertAfterBegin(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "afterBegin" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "afterBegin" });
  }
  return applyNode({ ...selectorOrOptions, position: "afterBegin" });
}
function insertAfterBeginLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "afterBegin", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "afterBegin", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "afterBegin", root: "light" });
}
function replaceChildrenLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "replaceChildren", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "replaceChildren", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "replaceChildren", root: "light" });
}
function clearChildrenLight(selectorOrOptions, maybeOptions, descriptor) {
  if (descriptor !== void 0 && (typeof maybeOptions === "symbol" || typeof maybeOptions === "string")) {
    return applyNode({ position: "clearChildren", root: "light" })(selectorOrOptions, maybeOptions, descriptor);
  }
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return applyNode(selectorOrOptions, { ...maybeOptions, position: "clearChildren", root: "light" });
  }
  return applyNode({ ...selectorOrOptions, position: "clearChildren", root: "light" });
}
const apply = applyNode;
const node = applyNode;
function applyAppHost(optionsOrTarget, propertyKey, descriptor) {
  if ((typeof propertyKey === "string" || typeof propertyKey === "symbol") && descriptor !== void 0) {
    return applyNode("$appHost", {})(optionsOrTarget, propertyKey, descriptor);
  }
  return applyNode("$appHost", optionsOrTarget);
}
function applyLight(selector, options) {
  return applyNode(selector, { ...options ?? {}, root: "light" });
}
function applyShadow(selector, options) {
  return applyNode(selector, { ...options ?? {}, root: "shadow" });
}
function applyAll(selector, options) {
  return applyNode(selector, { ...options ?? {}, root: "all" });
}
const findAllApplyNodeMetadata = (target) => {
  const result = /* @__PURE__ */ new Map();
  const maps = ReflectUtils.findAllMetadata(APPLY_NODE_METADATA_KEY, target);
  maps.forEach((map) => {
    if (map instanceof Map) {
      map.forEach((v, k) => result.set(k, v));
    }
  });
  return result;
};
const filterScopeOf = (target) => {
  if (target && typeof target.querySelector === "function") {
    return target;
  }
  const host = target?.host;
  return host && typeof host.querySelector === "function" ? host : null;
};
const tagNameOf = (node2) => {
  const tag = node2?.tagName;
  return typeof tag === "string" ? tag.toLowerCase() : null;
};
const skipIfSameTagPresent = (target, newValue) => {
  const nodes = (Array.isArray(newValue) ? newValue : [newValue]).filter((n) => tagNameOf(n) !== null);
  if (nodes.length === 0) return true;
  const scope = filterScopeOf(target);
  if (!scope) return true;
  return !nodes.some((n) => scope.querySelector(tagNameOf(n)));
};
const skipIfExists = (selector) => (target) => {
  const scope = filterScopeOf(target);
  if (!scope) return true;
  return !scope.querySelector(selector);
};
const skipIfEmpty = (_target, newValue) => {
  if (newValue === null || newValue === void 0 || newValue === false) return false;
  if (typeof newValue === "string") return newValue.trim().length > 0;
  if (Array.isArray(newValue)) return newValue.length > 0;
  return true;
};
const applyIfChanged = (target, newValue) => {
  const textOf = (v) => {
    if (typeof v === "string") return v;
    if (Array.isArray(v)) return v.map(textOf).join("");
    return typeof v?.textContent === "string" ? v.textContent : "";
  };
  const current = typeof target?.textContent === "string" ? target.textContent : "";
  return current !== textOf(newValue);
};
export {
  APPLY_NODE_METADATA_KEY,
  apply,
  applyAll,
  applyAppHost,
  applyIfChanged,
  applyLight,
  applyNode,
  applyShadow,
  clearChildrenLight,
  clearChildrenNode,
  clearNode,
  findAllApplyNodeMetadata,
  innerHtml,
  innerHtmlLight,
  innerHtmlShadow,
  innerText,
  innerTextLight,
  innerTextShadow,
  insertAfterBegin,
  insertAfterBeginLight,
  insertBeforeEnd,
  insertBeforeEndLight,
  insertBeforeEndShadow,
  node,
  removeNode,
  replaceChildren,
  replaceChildrenLight,
  skipIfEmpty,
  skipIfExists,
  skipIfSameTagPresent
};
//# sourceMappingURL=applyNode.js.map
